import type { ReactNode } from "react";
import { useState } from "react";

import { Button } from "../Button/Button";
import { Card } from "../Card/Card";
import { Checkbox } from "../Checkbox/Checkbox";
import { CopyField } from "../CopyField/CopyField";
import { QrCode } from "../QrCode/QrCode";
import { Dialog } from "../Dialog/Dialog";
import { Drawer } from "../Drawer/Drawer";
import { ImageCropEditor } from "../ImageCropEditor/ImageCropEditor";
import { InlineBanner } from "../InlineBanner/InlineBanner";
import { Input } from "../Input/Input";
import { Radio } from "../Radio/Radio";
import { SavedNote } from "../SavedNote/SavedNote";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl";
import { Select } from "../Select/Select";
import { Skeleton } from "../Skeleton/Skeleton";
import { Spinner } from "../Spinner/Spinner";
import { Switch } from "../Switch/Switch";
import { Tabs } from "../Tabs/Tabs";
import { Tooltip } from "../Tooltip/Tooltip";

/**
 * Every component in every variant, on one page.
 *
 * Two jobs:
 *  1. Visual review. Open it at 390px and at 1440px, and switch your OS
 *     appearance between light and dark. There is no in-page theme toggle
 *     because there is no theme toggle anywhere on the platform — light and
 *     dark follow `prefers-color-scheme`, so the honest way to review both is
 *     to change the device setting (or emulate it in browser devtools).
 *  2. The screenshot suite. `tests/e2e/src/gallery.spec.ts` captures each
 *     `data-gallery-section` element separately at 390px and 1440px in light
 *     and dark, driving the color scheme through Playwright's `emulateMedia`.
 *
 * A component with no entry here is invisible to both, which is why the
 * gallery entry must land in the same change as the component itself.
 *
 * `apps/app` mounts it at `/gallery` (`apps/app/src/routes/gallery.tsx`, behind
 * `requireSignedIn`). NOT `-gallery.tsx`: `apps/app/tsr.config.json` sets
 * `routeFileIgnorePrefix: "-"`, so a `-`-prefixed file is excluded from route
 * generation and produces no route at all.
 *
 *   import { Gallery } from "@sportsmet/ui/gallery";
 *   export const Route = createFileRoute("/gallery")({
 *     beforeLoad: requireSignedIn,
 *     component: Gallery,
 *   });
 */

const Section = ({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}) => (
  <section
    className="flex flex-col gap-4 border-t border-hairline pt-6"
    data-gallery-section={title}
  >
    <div className="flex flex-col gap-1">
      <h2 className="text-h1 text-ink">{title}</h2>
      {note ? <p className="text-caption text-ink-secondary">{note}</p> : null}
    </div>
    {children}
  </section>
);

/**
 * The gallery is a display surface, not a screen: its actions have nowhere to
 * go. One shared no-op stands in for the handlers a real screen would wire to
 * a mutation, so it is obvious at a glance that nothing here is unfinished.
 */
const noop = (): void => {
  // Intentionally does nothing. See above.
};

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-2">
    <span className="text-caption text-ink-secondary">{label}</span>
    <div className="flex flex-wrap items-center gap-3">{children}</div>
  </div>
);

const POOL_TYPES = [
  { value: "survivor", label: "Survivor" },
  { value: "pickem", label: "Pick'em" },
  { value: "squares", label: "Squares", disabled: true },
];

const TIE_POLICIES = [
  {
    value: "split",
    label: "Split the pot",
    description: "Everyone still alive shares it.",
  },
  {
    value: "rollover",
    label: "Roll it over",
    description: "The pot carries to next round.",
  },
  { value: "commissioner", label: "Commissioner decides", disabled: true },
];

/**
 * A sample picture for the crop editor section: blue sky, green field and a
 * sun in the top left corner, so a turn is obvious. An SVG needs no canvas to
 * make, and the editor opens it like any picked photo. Named colors, not
 * hex: the gallery's own test refuses a raw hex anywhere in this file.
 */
const CROP_SAMPLE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800"><rect width="1200" height="400" fill="royalblue"/><rect y="400" width="1200" height="400" fill="forestgreen"/><circle cx="240" cy="200" r="120" fill="gold"/></svg>';

const makeCropSampleFile = (): File =>
  new File([CROP_SAMPLE_SVG], "sample.svg", { type: "image/svg+xml" });

/** The gallery route's component. Renders every export of `@sportsmet/ui`. */
export const Gallery = (): ReactNode => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dirtyDialogOpen, setDirtyDialogOpen] = useState(false);
  const [drawerLeftOpen, setDrawerLeftOpen] = useState(false);
  const [drawerRightOpen, setDrawerRightOpen] = useState(false);
  const [poolType, setPoolType] = useState<string | undefined>(undefined);
  const [tiePolicy, setTiePolicy] = useState<string | undefined>(undefined);
  const [tab, setTab] = useState("all");
  const [keptTab, setKeptTab] = useState("notes");
  const [segment, setSegment] = useState("away");
  const [period, setPeriod] = useState("q1");
  const [agreed, setAgreed] = useState(false);
  const [gameStartAlert, setGameStartAlert] = useState(true);
  const [gameEndAlert, setGameEndAlert] = useState(false);
  const [cropSample, setCropSample] = useState<{
    preset: "banner" | "photo";
    file: File;
  } | null>(null);
  const [cropResultUrl, setCropResultUrl] = useState<string | null>(null);

  return (
    <div className="min-h-dvh bg-bg font-sans text-ink">
      <div className="sm-content-width flex flex-col gap-8 px-4 py-4">
        <header className="flex flex-col gap-2">
          <h1 className="text-display text-ink">Component gallery</h1>
          <p className="text-body text-ink-secondary">
            Every component in every variant. Review it at 390px first, then at
            1440px. Light and dark follow your device appearance — there is no
            in-app toggle anywhere on this platform, so switch your OS setting
            or emulate it in devtools to see the other mode.
          </p>
        </header>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Button"
          note="Exactly one primary per screen. Everything else is secondary, outline, ghost, or link."
        >
          <Row label="Variants">
            <Button variant="primary">Submit picks</Button>
            <Button variant="secondary">Cancel</Button>
            <Button variant="outline">Invite a friend</Button>
            <Button variant="ghost">Skip for now</Button>
            <Button variant="destructive">Leave pool</Button>
            <Button variant="link">Back to pools</Button>
          </Row>
          <Row label="Sizes — default 48px, compact 36px with a 44px tap target, icon 48x48">
            <Button size="md">Join pool</Button>
            <Button size="sm">Edit</Button>
            <Button size="icon" aria-label="Add an entry">
              <span aria-hidden="true">+</span>
            </Button>
          </Row>
          <Row label="Loading — the spinner belongs to the control that was tapped">
            <Button loading loadingLabel="Submitting picks">
              Submit picks
            </Button>
            <Button variant="secondary" loading>
              Cancel
            </Button>
            <Button variant="destructive" loading loadingLabel="Leaving pool">
              Leave pool
            </Button>
          </Row>
          <Row label="Disabled — one treatment across every variant">
            <Button disabled>Submit picks</Button>
            <Button variant="secondary" disabled>
              Cancel
            </Button>
            <Button variant="outline" disabled>
              Invite a friend
            </Button>
            <Button variant="destructive" disabled>
              Leave pool
            </Button>
          </Row>
          <Row label="Full width — the mobile primary CTA">
            <Button fullWidth>Submit picks</Button>
          </Row>
          <Row label="With icons">
            <Button iconLeading={<span aria-hidden="true">+</span>}>
              Create pool
            </Button>
            <Button
              variant="secondary"
              iconTrailing={<span aria-hidden="true">→</span>}
            >
              Next round
            </Button>
          </Row>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Input"
          note="Label above, never a placeholder standing in for one. 16px text so iOS Safari does not zoom."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Pool name" placeholder="Sunday crew" />
            <Input label="Pool name" defaultValue="Sunday crew" />
            <Input
              label="Join code"
              hint="Six characters, from your commissioner."
              inputMode="numeric"
            />
            <Input label="Pool name" required error="Enter a pool name." />
            <Input
              label="Entry fee"
              prefix="$"
              suffix="USD"
              inputMode="decimal"
            />
            <Input
              label="Pool name"
              disabled
              defaultValue="Locked for the season"
            />
            <Input
              label="Search by pool name"
              labelHidden
              placeholder="Search by pool name"
            />
            <Input label="Commissioner email" type="email" inputMode="email" />
          </div>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section title="Select">
          <div className="grid gap-4 md:grid-cols-2">
            <Select
              label="Pool type"
              items={POOL_TYPES}
              placeholder="Choose a pool type"
              value={poolType}
              onValueChange={setPoolType}
            />
            <Select label="Pool type" items={POOL_TYPES} value="pickem" />
            <Select
              label="Pool type"
              items={POOL_TYPES}
              hint="You can change this until the first kickoff."
            />
            <Select
              label="Pool type"
              items={POOL_TYPES}
              required
              error="Pick a pool type."
            />
            <Select
              label="Pool type"
              items={POOL_TYPES}
              disabled
              placeholder="Locked"
            />
            <Select
              label="Pool type"
              items={[]}
              placeholder="No pool types yet"
            />
          </div>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Checkbox"
          note="Solid fill plus on-primary ink when checked — never a washed tint as the only signal."
        >
          <div className="flex flex-col gap-2">
            <Checkbox
              label="Email me when a round locks"
              checked={agreed}
              onCheckedChange={(next) => {
                setAgreed(next === true);
              }}
            />
            <Checkbox
              label="Email me when a round locks"
              checked
              description="One email an hour before kickoff."
            />
            <Checkbox label="Select every entry" checked="indeterminate" />
            <Checkbox label="Email me when a round locks" disabled />
            <Checkbox label="Email me when a round locks" disabled checked />
            <Checkbox
              label="I agree to the pool rules"
              required
              error="Agree to the rules to join."
            />
          </div>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section title="Copy field">
          <Row label="Resting, a secret value with its own copy control">
            <CopyField
              label="Invite code"
              value="9F4K-2QRD"
              copyLabel="Copy invite code"
              hint="Secret. Anyone with it can join your pool."
            />
          </Row>
          <Row label="A long value wraps rather than pushing the sheet sideways">
            <CopyField
              label="Share link"
              value="https://sportsmet.com/join/9F4K-2QRD"
              copyLabel="Copy share link"
            />
          </Row>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section title="QR code">
          <Row label="Default, a pool's invite link">
            <QrCode
              value="https://app.sportsmet.com/join/9F4K2QRD"
              label="QR code for Sunday Crew"
            />
          </Row>
          <Row label="Large, for the full-screen scan view">
            <QrCode
              value="https://app.sportsmet.com/join/9F4K2QRD"
              label="QR code for Sunday Crew"
              size="lg"
            />
          </Row>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section title="Radio">
          <div className="grid gap-6 md:grid-cols-2">
            <Radio
              label="If there's a tie"
              items={TIE_POLICIES}
              value={tiePolicy}
              onValueChange={setTiePolicy}
            />
            <Radio
              label="If there's a tie"
              items={TIE_POLICIES}
              value="rollover"
            />
            <Radio
              label="If there's a tie"
              items={TIE_POLICIES}
              required
              error="Pick a tie policy."
            />
            <Radio
              label="Round length"
              orientation="horizontal"
              items={[
                { value: "week", label: "Weekly" },
                { value: "day", label: "Daily" },
              ]}
              value="week"
            />
          </div>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Dialog"
          note="A bottom sheet under 768px, a centered dialog at 768px and up — one component, both shapes."
        >
          <Row label="Open one">
            <Button
              variant="secondary"
              onClick={() => {
                setDialogOpen(true);
              }}
            >
              Open a form dialog
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setDirtyDialogOpen(true);
              }}
            >
              Open one with unsaved changes
            </Button>
          </Row>

          <Dialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            title="Create pool"
            description="You can change any of this until the first kickoff."
            footer={
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDialogOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setDialogOpen(false);
                  }}
                >
                  Create pool
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-4 pb-4">
              <Input label="Pool name" placeholder="Sunday crew" />
              <Select
                label="Pool type"
                items={POOL_TYPES}
                placeholder="Choose a pool type"
              />
              <Checkbox label="Let members invite other people" />
            </div>
          </Dialog>

          <Dialog
            open={dirtyDialogOpen}
            onOpenChange={setDirtyDialogOpen}
            onRequestClose={() => {
              // A real screen shows the shared discard confirm here. The
              // gallery just refuses to close, which is the visible proof
              // that Escape and the scrim do not throw typing away.
            }}
            isDirty
            title="Edit pool"
            description="Escape and the scrim ask before discarding. Use Cancel to leave."
            footer={
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDirtyDialogOpen(false);
                  }}
                >
                  Discard changes
                </Button>
                <Button
                  onClick={() => {
                    setDirtyDialogOpen(false);
                  }}
                >
                  Save pool
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-4 pb-4">
              <Input label="Pool name" defaultValue="Sunday crew" />
            </div>
          </Dialog>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Drawer"
          note="An edge-anchored panel at every width — navigation and filters, not transient forms."
        >
          <Row label="Open one">
            <Button
              variant="secondary"
              onClick={() => {
                setDrawerLeftOpen(true);
              }}
            >
              Open the left drawer
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setDrawerRightOpen(true);
              }}
            >
              Open the right drawer
            </Button>
          </Row>

          <Drawer
            open={drawerLeftOpen}
            onOpenChange={setDrawerLeftOpen}
            side="left"
            title="Menu"
          >
            <nav className="flex flex-col gap-2 pb-4">
              <Button variant="ghost" fullWidth>
                Pools
              </Button>
              <Button variant="ghost" fullWidth>
                Standings
              </Button>
              <Button variant="ghost" fullWidth>
                Scores
              </Button>
            </nav>
          </Drawer>

          <Drawer
            open={drawerRightOpen}
            onOpenChange={setDrawerRightOpen}
            side="right"
            title="Filters"
            description="Narrow the list to what you care about."
            footer={
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDrawerRightOpen(false);
                  }}
                >
                  Clear filters
                </Button>
                <Button
                  onClick={() => {
                    setDrawerRightOpen(false);
                  }}
                >
                  Apply filters
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-2 pb-4">
              <Checkbox label="Active" />
              <Checkbox label="Eliminated" />
              <Checkbox label="Finished" />
            </div>
          </Drawer>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Image crop editor"
          note="Pick a sample picture to try the editor. The framed result shows below after Use photo."
        >
          <Row label="Open it">
            <Button
              variant="secondary"
              onClick={() => {
                setCropSample({ preset: "banner", file: makeCropSampleFile() });
              }}
            >
              Try the banner frame
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setCropSample({ preset: "photo", file: makeCropSampleFile() });
              }}
            >
              Try the photo circle
            </Button>
          </Row>
          {cropResultUrl !== null ? (
            <img
              src={cropResultUrl}
              alt="The framed result"
              className="h-32 w-auto max-w-full rounded-md"
            />
          ) : null}
          <ImageCropEditor
            file={cropSample?.file ?? null}
            title={
              cropSample?.preset === "photo"
                ? "Frame your photo"
                : "Frame your banner"
            }
            aspect={cropSample?.preset === "photo" ? 1 : 3}
            shape={cropSample?.preset === "photo" ? "round" : "rect"}
            maxOutputEdgePx={1024}
            onCancel={() => {
              setCropSample(null);
            }}
            onConfirm={(cropped) => {
              setCropSample(null);
              setCropResultUrl((previous) => {
                if (previous !== null) URL.revokeObjectURL(previous);
                return URL.createObjectURL(cropped);
              });
            }}
            testId="gallery-image-crop"
          />
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Inline banner"
          note="How an outcome is announced. There are no toasts and no snackbars on this platform, for success or for error, on any platform including Android."
        >
          <Row label="Neutral">
            <div className="w-full">
              <InlineBanner message="Standings update about a minute after each game." />
            </div>
          </Row>
          <Row label="Success">
            <div className="w-full">
              <InlineBanner
                variant="success"
                message="Your picks are in for Week 3."
              />
            </div>
          </Row>
          <Row label="Warning">
            <div className="w-full">
              <InlineBanner
                variant="warning"
                message="This pool locks in 20 minutes."
              />
            </div>
          </Row>
          <Row label="Offline — a warm banner below the header, with a retry">
            <div className="w-full">
              <InlineBanner
                variant="offline"
                message="You're offline. We're showing what we saved."
                action={{ label: "Try again", onAction: noop }}
              />
            </div>
          </Row>
          <Row label="Busy, while the action the fan pressed is running">
            <div className="w-full">
              <InlineBanner
                variant="warning"
                message="We could not load your pools."
                action={{
                  label: "Try again",
                  onAction: noop,
                  loading: true,
                  loadingLabel: "Reloading your pools.",
                }}
              />
            </div>
          </Row>
          <Row label="Dismissible">
            <div className="w-full">
              <InlineBanner
                variant="success"
                message="Your picks are in for Week 3."
                onDismiss={noop}
              />
            </div>
          </Row>
        </Section>

        <Section
          title="Saved note"
          note="The persistent settings-field state. It stays until the value changes again."
        >
          <Row label="Saved">
            <SavedNote saved />
          </Row>
          <Row label="Custom label">
            <SavedNote saved label="Saved to your profile" />
          </Row>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Tooltip"
          note="Desktop and admin nicety only — never somewhere a fan on a phone needs to look."
        >
          <Row label="Sides">
            <Tooltip content="Locks at the first kickoff" side="top">
              <Button variant="secondary">Top</Button>
            </Tooltip>
            <Tooltip content="Locks at the first kickoff" side="right">
              <Button variant="secondary">Right</Button>
            </Tooltip>
            <Tooltip content="Locks at the first kickoff" side="bottom">
              <Button variant="secondary">Bottom</Button>
            </Tooltip>
            <Tooltip content="Locks at the first kickoff" side="left">
              <Button variant="secondary">Left</Button>
            </Tooltip>
          </Row>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Tabs"
          note="The active tab belongs in a query param, not component state."
        >
          <Tabs
            label="Pool status"
            value={tab}
            onValueChange={setTab}
            items={[
              {
                value: "all",
                label: "All",
                badge: 7,
                content: (
                  <p className="text-body text-ink-secondary">
                    Every pool you are in.
                  </p>
                ),
              },
              {
                value: "active",
                label: "Active",
                badge: 3,
                content: (
                  <p className="text-body text-ink-secondary">
                    Pools where you are still alive.
                  </p>
                ),
              },
              {
                value: "eliminated",
                label: "Eliminated",
                badge: 4,
                content: (
                  <p className="text-body text-ink-secondary">
                    Pools you are out of.
                  </p>
                ),
              },
              {
                value: "archived",
                label: "Archived",
                content: (
                  <p className="text-body text-ink-secondary">Wrapped up.</p>
                ),
                disabled: true,
              },
            ]}
          />

          {/* `keepMounted` holds every panel in the document, so a
              half-filled field survives a trip to another tab and back. It
              lives inside this same Section rather than a Section of its own:
              a new Section mints four new visual baselines, and those are
              produced only by the visual-baselines workflow. */}
          <Tabs
            label="Pool sections"
            keepMounted
            value={keptTab}
            onValueChange={setKeptTab}
            items={[
              {
                value: "notes",
                label: "Notes",
                content: (
                  <Input
                    label="Your note"
                    placeholder="Type here, switch tabs, come back"
                  />
                ),
              },
              {
                value: "rules",
                label: "Rules",
                content: (
                  <p className="text-body text-ink-secondary">
                    Last one standing takes the pot.
                  </p>
                ),
              },
            ]}
          />
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Segmented control"
          note="A secondary switch inside one view: a team, a period, a grouping. Never a page tab row."
        >
          <SegmentedControl
            label="Box score team"
            options={[
              { value: "away", label: "KC" },
              { value: "home", label: "BUF" },
            ]}
            value={segment}
            onValueChange={setSegment}
            fullWidth
          />
          <SegmentedControl
            label="Period"
            options={[
              { value: "q1", label: "1st" },
              { value: "q2", label: "2nd" },
              { value: "q3", label: "3rd" },
              { value: "q4", label: "4th" },
              { value: "ot", label: "OT" },
            ]}
            value={period}
            onValueChange={setPeriod}
          />
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Switch"
          note="Writes on flip, with no save step -- Checkbox stays the right control for a form field submitted later."
        >
          <div className="flex flex-col gap-2">
            <Switch
              label="Game start"
              checked={gameStartAlert}
              onCheckedChange={setGameStartAlert}
            />
            <Switch
              label="Game end"
              description="Carries the final score"
              checked={gameEndAlert}
              onCheckedChange={setGameEndAlert}
            />
            <Switch
              label="Halftime"
              checked={false}
              onCheckedChange={noop}
              disabled
            />
          </div>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Card"
          note="At most ONE tinted card per screen, for the single most important thing on it."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Card
              title="Sunday crew"
              badge={
                <span className="rounded-full bg-success-solid px-2 text-caption text-on-success">
                  Alive
                </span>
              }
            >
              <p className="text-body text-ink-secondary">
                Week 3 · 12 entries · locks 1:00 PM ET
              </p>
            </Card>

            <Card title="This week's pick" tone="tinted" elevation="raised">
              <p className="text-body text-ink">You have the Cowboys to win.</p>
            </Card>

            <Card title="Round graded" tone="success">
              <p className="text-body text-ink">You advanced to Week 4.</p>
            </Card>

            <Card title="Locking soon" tone="warning">
              <p className="text-body text-ink">Picks close in 42 minutes.</p>
            </Card>

            <Card title="Eliminated" tone="error">
              <p className="text-body text-ink">
                Your entry is out as of Week 3.
              </p>
            </Card>

            <Card
              title="Office pool"
              padding="compact"
              footer={
                <span className="text-caption text-ink-secondary">
                  Commissioner: you
                </span>
              }
              onClick={noop}
            >
              <p className="text-body text-ink-secondary">
                Tap anywhere on this card.
              </p>
            </Card>

            <Card padding="none">
              <ul className="divide-y divide-hairline">
                <li className="p-4 text-body text-ink">Row one</li>
                <li className="p-4 text-body text-ink">Row two</li>
                <li className="p-4 text-body text-ink">Row three</li>
              </ul>
            </Card>

            <Card title="Pot total" elevation="raised">
              <p className="text-money-hero text-ink">$1,250.00</p>
            </Card>
          </div>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Skeleton"
          note="Shaped like the content that is coming — never a spinner over a screen that already has content."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <Skeleton
                shape="title"
                width="w-1/2"
                label="Loading the pool name"
              />
              <Skeleton lines={3} label="Loading pool details" />
            </Card>
            <Card>
              {/*
                The team-name placeholder is fluid, never a fixed `w-*` step.
                Tailwind's `--spacing` is `0.25rem`, so `w-32` is `8rem`, which
                is 256px once a reader doubles their browser base font size.
                That fixed 256px became the intrinsic minimum of this card, then
                of the grid column, then of the page, and pushed `/gallery` 100px
                wider than a 390px phone. See ISSUE-447.
              */}
              <div className="flex items-center gap-3">
                <Skeleton
                  shape="circle"
                  width="w-10"
                  className="w-auto"
                  label="Loading the team mark"
                />
                <Skeleton
                  shape="caption"
                  width="w-1/2"
                  className="min-w-0 flex-1"
                  label="Loading the team name"
                />
              </div>
            </Card>
            <Skeleton shape="control" label="Loading a control" />
            <Skeleton shape="card" label="Loading your pools" />
          </div>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Spinner"
          note="The control-scoped loading mark. Size is a class the caller passes. Button uses the medium icon. InlineBanner uses the small icon with shrink-0."
        >
          <Row label="Medium, the Button size">
            <Spinner
              className="h-icon-md w-icon-md"
              data-testid="gallery-spinner-md"
            />
          </Row>
          <Row label="Small, the InlineBanner size">
            <Spinner
              className="h-icon-sm w-icon-sm shrink-0"
              data-testid="gallery-spinner-sm"
            />
          </Row>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Type scale"
          note="Money and scores use tabular figures so a column never jitters as digits change."
        >
          <div className="flex flex-col gap-2">
            <p className="text-display text-ink">Display — 12 alive</p>
            <p className="text-h1 text-ink">Heading 1 — Your pools</p>
            <p className="text-h2 text-ink">Heading 2 — This week's picks</p>
            <p className="text-body text-ink">
              Body — the default text on every screen.
            </p>
            <p className="text-body-strong text-ink">
              Body strong — a list title.
            </p>
            <p className="text-caption text-ink-secondary">
              Caption — sentence case, never an uppercase overline.
            </p>
            <p className="text-small text-ink-tertiary">
              Small — legal text and fine print.
            </p>
            <p className="text-money-hero text-ink">$1,250.00</p>
            <p className="text-money-kpi text-ink">$425.00</p>
            <p className="text-money-total text-ink">$75.00</p>
            <p className="text-money-row text-ink">$25.00</p>
            <p className="text-score text-ink">24 — 17</p>
          </div>
        </Section>

        {/* ---------------------------------------------------------- */}
        <Section
          title="Color roles"
          note="Every pair below is asserted at its WCAG 2.2 AA floor by packages/theme's tokens.test.ts."
        >
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-md bg-primary-solid p-4 text-on-primary">
              primary-solid / on-primary
            </div>
            <div className="rounded-md bg-primary-tint p-4 text-primary">
              primary-tint / primary
            </div>
            <div className="rounded-md bg-accent-solid p-4 text-on-accent">
              accent-solid / on-accent
            </div>
            <div className="rounded-md bg-accent-tint p-4 text-accent">
              accent-tint / accent
            </div>
            <div className="rounded-md bg-success-solid p-4 text-on-success">
              success-solid / on-success
            </div>
            <div className="rounded-md bg-success-tint p-4 text-success">
              success-tint / success
            </div>
            <div className="rounded-md bg-warning-solid p-4 text-on-warning">
              warning-solid / on-warning
            </div>
            <div className="rounded-md bg-warning-tint p-4 text-warning">
              warning-tint / warning
            </div>
            <div className="rounded-md bg-error-solid p-4 text-on-error">
              error-solid / on-error
            </div>
            <div className="rounded-md bg-error-tint p-4 text-error">
              error-tint / error
            </div>
            <div className="rounded-md bg-info-solid p-4 text-on-info">
              info-solid / on-info
            </div>
            <div className="rounded-md bg-info-tint p-4 text-info">
              info-tint / info
            </div>
            <div className="rounded-md bg-surface p-4 text-ink">
              surface / ink
            </div>
            <div className="rounded-md bg-surface-sunken p-4 text-ink-secondary">
              surface-sunken / ink-secondary
            </div>
            <div className="rounded-md bg-ink p-4 text-ink-inverse">
              ink / ink-inverse
            </div>
            <div className="rounded-md border border-line bg-surface p-4 text-ink-tertiary">
              border / ink-tertiary
            </div>
          </div>
        </Section>

        <footer className="border-t border-hairline pt-6 pb-10">
          <p className="text-small text-ink-tertiary">
            sportsmet design system · @sportsmet/ui on @sportsmet/theme
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Gallery;
